import { SliceBox } from "@cms/components/SliceBox";
import { useSliceContext } from "@cms/context";
import type { Content } from "@prismicio/client";
import type { SliceComponentProps } from "@prismicio/react";
import UIKitMediaCarousel, {
  type MediaCarouselProps as UIKitMediaCarouselProps,
} from "@ui-kit/MediaCarousel";
import { useCallback, useMemo } from "react";
import { v4 as uuidv4 } from "uuid";

/**
 * Props for `MediaCarousel`.
 */
export type MediaCarouselProps =
  SliceComponentProps<Content.MediaCarouselSlice>;

/**
 * Interface for the context data used in MediaCarousel.
 */
type MediaCarouselContextData = {
  game: Content.GameDocument;
  mediaCarousel?: {
    onSlideChange?: (url: string) => void;
  };
};

const getSlidesFromSlice = (
  slice: Content.MediaCarouselSlice,
): UIKitMediaCarouselProps["slides"] => {
  return slice.primary.slides.map((slide) => ({
    id: uuidv4(),
    video_url: slide.video_url || "",
    video_mime_type: (slide.video_mime_type || "") as string,
    image: {
      url: slide.image.url || "",
      alt: slide.image.alt || "",
    },
  }));
};

/**
 * Component for "MediaCarousel" Slices.
 */
const MediaCarousel = ({ slice, index }: MediaCarouselProps) => {
  const ctx = useSliceContext<MediaCarouselContextData>();
  const { mediaCarousel } = ctx.data;

  const onSlideChange = useCallback(
    (url: string) => {
      mediaCarousel?.onSlideChange?.(url);
    },
    [mediaCarousel],
  );
  const slides = useMemo(() => getSlidesFromSlice(slice), [slice]);

  return (
    <SliceBox slice={slice} index={index}>
      <UIKitMediaCarousel slides={slides} onSlideChange={onSlideChange} />
    </SliceBox>
  );
};

export default MediaCarousel;
